/**
 * Christmas badges — authored via the `/pack` toile process (plan
 * `thinking/plans/2026_08_31_toile_pack_wiring.md` Track B), wave `toile_seasons`. Narcis file-split
 * shape (Track A): one module per pack, `registry.ts` assembles them into `livePacks`.
 */
import {tagline, chips, type ToilePack} from './data'

export const christmasBadgesPack: ToilePack = {
    slug: 'christmas-badges',
    category: 'season',
    name: 'Christmas badges',
    tagline: tagline('pieces', 'themes', 'styles'),
    chips: chips('themes', 'styles'),
    ratio: {w: 1024, h: 1024},
    readings: [
        {key: 'seal', label: 'Round seal', subject: 'a round badge lettered "MERRY CHRISTMAS" around the rim with a tree at the centre'},
        {key: 'ribbon', label: 'Ribbon badge', subject: 'a ribbon badge lettered "EST. DEC 25" with a sprig of holly'},
        {key: 'stamp', label: 'Postmark stamp', subject: 'a round postmark-style stamp lettered "NORTH POLE" with a snowflake'},
    ],
    styles: [
        {key: 'scandi', label: 'Scandi folk', style: 'scandinavian folk illustration, simplified shapes, no outlines, matte flat colour', palette: 'A limited palette of red, white, forest green and a touch of gold.', framing: 'Centered on a solid cream background, built to read clean at small size.'},
        {key: 'vintage', label: 'Vintage card', style: 'mid-century christmas card illustration, soft halftone grain, slight misregistration', palette: 'Muted red, pine green, cream and a faded gold.', framing: 'Centered on an aged cream paper background with an even margin.'},
        {key: 'cozy', label: 'Cozy cartoon', style: 'cozy cartoon illustration, rounded shapes, thick soft outlines, warm and friendly', palette: 'Warm reds and greens with a glowing amber and snow white.', framing: 'Centered on a solid background with a little room around it.'},
    ],
    directions: ['a family crest instead', 'for a wax seal', 'in two colors instead of one'],
    directionHint: 'try: a family crest instead',
    seo: {
        title: 'Christmas Badges: Seal, Ribbon, Postmark Designs',
        metaDescription: 'Nine christmas badges -- round seal, ribbon badge, postmark stamp -- in scandi folk, vintage card, and cozy cartoon finishes.',
        h1: 'Christmas badge design: round seal, ribbon badge, and postmark stamp',
        tagline: 'Nine marks, three badge shapes, three finishes each.',
        intro: "Nine christmas badge and logo-mark designs: a round seal lettered 'Merry Christmas', a ribbon badge marked 'Est. Dec 25', and a North Pole postmark stamp. Three finishes -- scandi folk, vintage card, and cozy cartoon -- for a shop logo, a gift tag, or a wax-seal-style sticker. Ask for a family crest instead, or two colors, and the same three finishes carry it.",
        useCases: [
            'a shop or product logo mark',
            'a gift tag or sticker seal',
            'a holiday packaging accent',
            'a stamp-style email icon',
        ],
        faq: [
            {q: 'What does this pack actually look like?', a: 'Nine badge designs -- a round seal, a ribbon badge, and a postmark stamp -- each in scandi folk, vintage card, and cozy cartoon finishes, lettered and one focal shape.'},
            {q: 'Can I use this as a real logo?', a: 'Yes -- the badges work as a standalone mark for a seasonal shop logo, packaging, or social profile.'},
            {q: 'Can I get a different mark, like a family crest?', a: 'Yes -- the direction field takes any variation, including a family crest instead, a wax-seal texture, or two colors instead of one.'},
            {q: 'What size does this print at?', a: 'Each badge renders as a 1024x1024 square, scaling cleanly from a small sticker to a printed gift tag.'},
        ],
    },
}
